import { useEffect, useMemo, useRef, useState } from 'react';
import { BODY_TYPE_GROUPS, BODY_TYPE_LABEL, type BodyType } from 'shared';
import Icon from './Icon';
import Modal from './Modal';
import { haptic } from './telegram';

interface Props {
  value: BodyType[];
  onChange: (v: BodyType[]) => void;
  /** Кузова в быстрых кнопках — свои у каждого, см. профиль. */
  favorites?: BodyType[];
  label?: string;
}

/**
 * Выбор кузовов: быстрые кнопки снаружи, поиск и плоский список внутри.
 *
 * На форме публикации на виду только выбранное и то, что человек сам
 * вынес в быстрые кнопки: сорок два типа чипами — это полторы высоты
 * экрана до цены, и так на каждой публикации.
 *
 * Внутри окна список плоский, с заголовками групп. Раньше группы были
 * гармошкой: девять свёрнутых заголовков, раскрывается один. Это два
 * решения вместо одного — сначала угадай группу, потом найди в ней
 * кузов, — и цена ошибки высокая: не угадал, закрой, открой другую.
 * Прокрутить один список глазами быстрее, чем перебрать девять ящиков.
 *
 * Поиск получает фокус при открытии. С клавиатуры выбор кузова — это
 * «набрал „реф“ и нажал», и требовать перед этим попасть курсором в
 * поле значит вставить лишний шаг в самое частое действие.
 */
export default function BodyTypePicker({ value, onChange, favorites = [], label }: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const search = useRef<HTMLInputElement>(null);

  /*
   * Фокус в поиск при открытии.
   *
   * С задержкой в кадр: окно только что появилось, и фокус, поставленный
   * до вставки в документ, теряется. На телефоне это заодно поднимает
   * клавиатуру — там это тоже верно, выбор почти всегда начинается с
   * набора первых букв.
   */
  useEffect(() => {
    if (!open) return;
    const t = window.setTimeout(() => search.current?.focus(), 60);
    return () => window.clearTimeout(t);
  }, [open]);

  /*
   * Что показывать: те же группы, но отфильтрованные запросом.
   *
   * Поиск не превращает список в плоскую кучу без заголовков — «Тент» и
   * «Тентованный полуприцеп» рядом без пометки, из какой они группы,
   * различаются плохо. Пустые после фильтра группы просто не выводятся.
   */
  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return BODY_TYPE_GROUPS;
    return BODY_TYPE_GROUPS.map((g) => ({
      ...g,
      types: g.types.filter((t) => BODY_TYPE_LABEL[t].toLowerCase().includes(q)),
    })).filter((g) => g.types.length > 0);
  }, [query]);

  function toggle(t: BodyType) {
    haptic();
    onChange(value.includes(t) ? value.filter((x) => x !== t) : [...value, t]);
  }

  /* Быстрые кнопки: избранное плюс уже выбранное — чтобы снять выбор
     можно было там же, не открывая список. */
  const quick = [...new Set([...value, ...favorites])];

  return (
    <>
      {quick.length > 0 && (
        <div className="chips" style={{ marginBottom: 8 }}>
          {quick.map((t) => (
            <button
              key={t}
              type="button"
              className={`chip ${value.includes(t) ? 'on' : ''}`}
              onClick={() => toggle(t)}
            >
              {BODY_TYPE_LABEL[t]}
            </button>
          ))}
        </div>
      )}

      <button
        type="button"
        className="picker-trigger"
        onClick={() => {
          setQuery('');
          setOpen(true);
        }}
      >
        <span className="picker-trigger__value">
          {value.length === 0
            ? 'Выбрать кузов'
            : `Выбрано: ${value.length}`}
        </span>
        <Icon name="down" size={16} />
      </button>

      <Modal open={open} onClose={() => setOpen(false)} title={label ?? 'Тип кузова'}>
        {/* Поиск липнет к верху: список длинный, и уточнить запрос надо
            уметь с любой его высоты, не прокручивая обратно. */}
        <div className="bodypick__search">
          <input
            ref={search}
            placeholder="Найти кузов: тент, реф, трал…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>

        {/* Выбранное — строкой над списком: снять отметку можно там же,
            где видно, что отмечено, не отыскивая пункт в списке заново. */}
        {value.length > 0 && (
          <div className="bodypick__picked">
            {value.map((t) => (
              <button
                key={t}
                type="button"
                className="chip on"
                onClick={() => toggle(t)}
                title="Убрать"
              >
                {BODY_TYPE_LABEL[t]} ×
              </button>
            ))}
          </div>
        )}

        <div className="bodypick__list">
          {shown.length === 0 && <div className="empty">Ничего не нашлось</div>}

          {shown.map((g) => (
            <div key={g.title} className="bodypick__group">
              <div className="bodypick__title">{g.title}</div>
              {g.types.map((t) => (
                <button
                  key={t}
                  type="button"
                  className={`bodypick__item${value.includes(t) ? ' is-on' : ''}`}
                  onClick={() => toggle(t)}
                >
                  <span>{BODY_TYPE_LABEL[t]}</span>
                  {value.includes(t) && <Icon name="check" size={16} />}
                </button>
              ))}
            </div>
          ))}
        </div>

        {/* Кнопка закрытия внизу: выбор множественный, и окно не должно
            захлопываться после каждой отметки. */}
        <button type="button" className="bodypick__done" onClick={() => setOpen(false)}>
          {value.length > 0 ? `Готово · выбрано ${value.length}` : 'Закрыть'}
        </button>
      </Modal>
    </>
  );
}

/**
 * Тот же выбор, но одного кузова — для фильтров и карточки техники.
 *
 * Обёртка над множественным выбором, а не отдельный компонент: список
 * кузовов, поиск и группы должны быть одни и те же, иначе они разъедутся
 * на первой же правке справочника.
 */
export function BodyTypeOne({
  value,
  onChange,
  anyLabel,
  favorites,
  label,
}: {
  value: BodyType | '';
  onChange: (v: BodyType | '') => void;
  /** Текст пункта «любой». Пусто — сбросить выбор нельзя. */
  anyLabel?: string;
  favorites?: BodyType[];
  label?: string;
}) {
  return (
    <>
      <BodyTypePicker
        value={value ? [value] : []}
        // Из множественного выбора берём последний отмеченный: нажатие на
        // другой кузов должно заменять текущий, а не копить набор.
        onChange={(v) => onChange(v.length === 0 ? '' : (v[v.length - 1] as BodyType))}
        favorites={favorites}
        label={label}
      />
      {anyLabel && value && (
        <button
          type="button"
          className="chip chip--ghost"
          style={{ marginTop: 'var(--s2)' }}
          onClick={() => onChange('')}
        >
          {anyLabel}
        </button>
      )}
    </>
  );
}
